import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import tw from "tailwind-styled-components";
import {
    BoxContainer,
    BoxTitle,
    BoxHead,
    Underline,
    FilterTableWrapper,
    FilterWrapperWithSearch,
} from "../../Components/Styles/PageStyles";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import axios from "axios";
import Config from "../../Config";
import Loading from "../../Components/Loading";
import NotFoundModel from "../../Components/NotFoundModel";
import SearchFilterBox from "../../Components/SearchFilterBox";
import SideModal from "../../Components/SideModal";
import DateRange from "../../Components/DateRange";
import Table from "../../Components/Inventory/PurchaseInventory/Table";
import ViewPurchaseDetails from "../../Components/Inventory/PurchaseInventory/ViewPurchaseDetails";
import { Form, Formik } from "formik";
import AddPurchase from "../../Components/Inventory/PurchaseInventory/AddPurchase";
import SummaryCards from "../../Components/Inventory/PurchaseInventory/SummaryCards";
import { SummarySkeletonLoading } from "../../Components/SkeletonLoading";

const PurchaseInventory = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isShowAddPurchase, setIsShowAddPurchase] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const user = useSelector((state) => state.UserReducer.user);
    const [apiData, setApiData] = useState([]);
    const [itemList, setItemList] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState("");
    const [startDate, setStartDate] = useState(
        moment().subtract(7, "days").format("YYYY-MM-DD")
    );
    const [endDate, setEndDate] = useState(
        moment(new Date()).format("YYYY-MM-DD")
    );
    const [previousStartDate, setPreviousStartDate] = useState(
        moment().subtract(7, "days").format("YYYY-MM-DD")
    );
    const [previousEndDate, setPreviousEndDate] = useState(
        moment(new Date()).format("YYYY-MM-DD")
    );

    const [summaryData, setSummaryData] = useState();
    const [showPurchaseDetails, setShowPurchaseDetails] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const searchOptions = [
        // { label: "Item", value: 1 },
        { label: "Vendor", value: 1 },
        { label: "Invoice", value: 2 },
    ];

    const panelRef = useRef(null);
    const btnRef = useRef(null);

    // Get date range display text
    const getDateRangeDisplayText = () => {
        if (
            startDate &&
            endDate &&
            moment(startDate).isValid() &&
            moment(endDate).isValid()
        ) {
            return (
                moment(startDate).format("DD MMM yyyy") +
                " - " +
                moment(endDate).format("DD MMM yyyy")
            );
        }
        return "Select Date Range";
    };

    // Fetch stock transfers
    const fetchFunction = async () => {
        const params = new URLSearchParams();

        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (searchQuery) {
            params.append("s", searchQuery);
            if (filterType) {
                params.append("t", filterType);
            }
        }

        const response = await axios.get(
            `${Config.apiUrl}/kitchen/inventory/purchase/ledger?${params.toString()}`,
            {
                headers: {
                    authorization: `Bearer ${user.token}`,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const getSuccess = (res) => {
        if (res.isSuccess === false) {
            toast.error(res.msg || "Failed to fetch purchase inventory");
        } else {
            const inventoryPurchaseLedger = res.inventoryPurchaseLedger || [];
            setApiData(inventoryPurchaseLedger);
            setItemList(res.inventoryItems || []);
            setSummaryData(res.purchaseSummary);
            setIsFocused(true);

        }
    };

    const getError = () => {
        navigate("/no-access");

    };

    const {
        isLoading: isFetching,
        mutate: getListMutate,
        error,
        data,
        isRefetching,
    } = useMutation(fetchFunction, {
        onSuccess: getSuccess,
        onError: getError,
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            getListMutate();
        }, 200);
        return () => clearTimeout(handler);
    }, [searchQuery, filterType, startDate, endDate]);

    // Handle search
    const handleFilterChange = (values) => {
        setSearchQuery(values.searchQuery || "");
        setFilterType(values.filterType || "");
    };

    // Handle date range submit
    const submitHandler = (values) => {
        setFilterType("");
        setSearchQuery("");
        const newValues = {
            startDate: moment(values.startDate).format("YYYY-MM-DD"),
            endDate: moment(values.endDate).format("YYYY-MM-DD"),
        };
        setStartDate(newValues.startDate);
        setEndDate(newValues.endDate);
        setPreviousStartDate(newValues.startDate);
        setPreviousEndDate(newValues.endDate);
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchQuery("");
        setFilterType("");
        setStartDate(moment().subtract(7, "days").format("YYYY-MM-DD"));
        setEndDate(moment(new Date()).format("YYYY-MM-DD"));
        setPreviousStartDate(moment().subtract(7, "days").format("YYYY-MM-DD"));
        setPreviousEndDate(moment(new Date()).format("YYYY-MM-DD"));
    };

    //------- Add Purchase -------
    const addPurchaseFunction = async (values) => {
        return await axios.post(`${Config.apiUrl}/kitchen/inventory/purchase/add`, values, {
            headers: {
                authorization: "Bearer" + " " + user.token,
                "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                // "Content-Type": "application/json",
            },
        });
    };

    const onAddPurchaseSuccess = (data) => {
        if (data?.data?.isSuccess === true) {
            toast.success(data?.data?.msg || "Success");
            getListMutate();
            setIsShowAddPurchase(false);
        } else {
            //   setIsShowAddTransfer(false);
            toast.error(data?.data?.msg || "An Error Occured");
        }
    };

    const onAddPurchaseError = (data) => {
        toast.error(data?.response?.data?.msg || "An Error Occured");
    };

    const { isLoading: addPurchaseLoading, mutate: addPurchaseMutate } = useMutation(
        addPurchaseFunction,
        {
            onSuccess: onAddPurchaseSuccess,
            onError: onAddPurchaseError,
        }
    );
    return (
        <BoxContainer>
            <BoxHead>
                <BoxTitle className="!mb-0 whitespace-nowrap">
                    Purchases
                    <Underline />
                </BoxTitle>
                <Filters
                    setIsShowAddPurchase={setIsShowAddPurchase}
                />
            </BoxHead>
            {(isFetching || isRefetching) && !isFocused && (
                <SummarySkeletonLoading count={3} />
            )}
            {/* Summary Cards */}
            {isFocused && summaryData && (
                <>
                    <SummaryCards summaryData={summaryData} />
                    <FilterTableWrapper>
                        <FilterWrapperWithSearch>
                            <div className="flex md:space-x-4 md:flex-row flex-col md:gap-0 gap-4">
                                <SearchFilterBox
                                    options={searchOptions}
                                    handleFilterChange={handleFilterChange}
                                    handleClearFilters={handleClearFilters}
                                    className="flex-1 min-w-0"
                                />
                                <DateRange
                                    submitHandler={submitHandler}
                                    defaultLabel={getDateRangeDisplayText()}
                                    className="flex-1 min-w-0"
                                />
                            </div>

                        </FilterWrapperWithSearch>
                        <SideModal
                            isOpen={showPurchaseDetails}
                            onClose={() => setShowPurchaseDetails(false)}
                        >
                            <ViewPurchaseDetails
                                setShowPurchaseDetails={setShowPurchaseDetails}
                                selectedData={selectedPurchase}
                            />
                        </SideModal>
                        <SideModal
                            isOpen={isShowAddPurchase}
                            onClose={() => setIsShowAddPurchase(false)}
                        >
                            <AddPurchase
                                handleClose={() => setIsShowAddPurchase(false)}
                                itemList={itemList}
                                addPurchaseMutate={addPurchaseMutate}
                                addPurchaseLoading={addPurchaseLoading}
                                user={user}
                            />
                        </SideModal>
                        {!error && !isFetching && data && (
                            <TableWrapper>
                                <Table
                                    apiData={error ? [] : apiData}
                                    setShowPurchaseDetails={setShowPurchaseDetails}
                                    setSelectedPurchase={setSelectedPurchase}

                                />
                                {(error || apiData.length === 0) && !isFetching && (
                                    <NotFoundModel msg={"No purchase inventory found."} />
                                )}
                            </TableWrapper>
                        )}
                    </FilterTableWrapper>
                </>
            )}

            {(isFetching || isRefetching) && <Loading />}
        </BoxContainer>
    );
};
const Filters = ({
    setIsShowAddPurchase,
}) => {
    return (
        <Formik >
            <Form>
                <FilterContainer>
                    <AddBtn
                        type="button"
                        onClick={() => {
                            setIsShowAddPurchase(true);
                        }}
                    >
                        {"Add Purchase"}
                    </AddBtn>
                </FilterContainer>
            </Form>
        </Formik>
    );
};
const TableWrapper = tw.h1`mt-4 bg-white rounded-md`;
const FilterBtn = tw.button`flex items-center border whitespace-nowrap border-gray-300 justify-center rounded-md px-4 cursor-pointer bg-white hover:bg-gray-200 h-10 text-sm ml-auto`;
const AddBtn = tw.button`${(p) =>
    p.$active
        ? "bg-gray-100"
        : ""} border whitespace-nowrap border-gray-200 flex space-x-1.5 justify-center items-center text-white h-full rounded-md px-6  cursor-pointer bg-gradient-to-r from-primary-color to-[#00A0FF] hover:brightness-110 py-2.5 text-sm`;
const FilterContainer = tw.div`grid grid-cols-2 md:grid-cols-3 w-full gap-2 lg:gap-0 md:flex md:space-x-4 items-end mt-0 md:mt-0`;
export default PurchaseInventory;